import { getCurrentScope, onScopeDispose, ref, watch } from 'vue'

const DEFAULT_DURATION_MS = 600

/**
 * Tweens a numeric source toward its new value (ease-out) so credit counters
 * and stats "count" instead of jumping. Falls back to an instant jump under
 * prefers-reduced-motion, for non-finite/negative sentinel values (e.g. -1 =
 * unlimited credits), and in environments without matchMedia.
 */
export function useCountUp(source: () => number, options?: { duration?: number }) {
	const duration = options?.duration ?? DEFAULT_DURATION_MS
	const value = ref(source())
	let frame: number | null = null

	const reducedMotion =
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches

	function cancel() {
		if (frame !== null) cancelAnimationFrame(frame)
		frame = null
	}

	watch(source, (to, from) => {
		cancel()
		if (reducedMotion || !Number.isFinite(to) || !Number.isFinite(from) || to < 0 || from < 0) {
			value.value = to
			return
		}
		const start = performance.now()
		const step = (now: number) => {
			const t = Math.min(1, (now - start) / duration)
			const eased = 1 - Math.pow(1 - t, 3)
			value.value = Math.round(from + (to - from) * eased)
			frame = t < 1 ? requestAnimationFrame(step) : null
		}
		frame = requestAnimationFrame(step)
	})

	if (getCurrentScope()) {
		onScopeDispose(cancel)
	}

	return { value }
}
